"use client";

import { CalendarDate } from "@internationalized/date";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { format, isValid } from "date-fns";
import { CalendarIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/cn";

import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import type { DateValue } from "@internationalized/date";

export interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  presentation?: "dialog" | "popover";
  popoverSide?: React.ComponentProps<typeof PopoverContent>["side"];
}

// Convert JS Date to CalendarDate
function toCalendarDate(date: Date | undefined): CalendarDate | undefined {
  if (!date || !isValid(date)) return undefined;
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

// Convert CalendarDate to JS Date
function toJsDate(calendarDate: DateValue): Date {
  return new Date(calendarDate.year, calendarDate.month - 1, calendarDate.day);
}

export function DatePicker({
  value,
  onChange,
  placeholder = "DD/MM/YYYY",
  className,
  disabled,
  minDate,
  maxDate,
  presentation = "dialog",
  popoverSide = "top",
}: DatePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  // Convert value to CalendarDate for react-aria Calendar
  const calendarValue = toCalendarDate(value);

  // Handle change from calendar
  const handleCalendarChange = (date: DateValue) => {
    onChange?.(toJsDate(date));
    setIsOpen(false);
  };

  // Format display value
  const displayValue = value && isValid(value) ? format(value, "dd/MM/yyyy") : "";

  const trigger = (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        // Токены Figma (input/color-*-input-enabled): альфа-оверлеи считаются от
        // neutral-50 (#f9f9f9). Раньше здесь стоял neutral-200 (#bfbfbf) — на фоне
        // #060606 такая подложка практически не читалась.
        "flex h-12 w-full items-center gap-2.5 rounded-sm bg-neutral-50/4 px-4 py-3",
        "border-2 border-transparent transition-colors",
        "focus:border-primary-800/63 hover:border-neutral-50/22",
        "cursor-pointer outline-none",
        disabled && "pointer-events-none cursor-not-allowed opacity-50",
        className
      )}
    >
      {/* Display value */}
      <span
        className={cn(
          "fs-300 min-w-0 flex-1 text-left font-semibold",
          displayValue ? "text-white" : "text-neutral-50/22"
        )}
      >
        {displayValue || placeholder}
      </span>

      {/* Calendar icon */}
      <CalendarIcon className="text-primary-400 size-6 shrink-0" />
    </button>
  );

  const calendar = (
    <Calendar
      value={calendarValue}
      onChange={handleCalendarChange}
      minValue={toCalendarDate(minDate)}
      maxValue={toCalendarDate(maxDate)}
    />
  );

  if (presentation === "popover") {
    return (
      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>

        <PopoverContent
          side={popoverSide}
          align="center"
          sideOffset={8}
          className={cn(
            // Layout
            "w-76 overflow-hidden p-2",
            // Background & Border
            "glass-blur rounded-md border-none bg-neutral-50/2 shadow-none"
          )}
        >
          {calendar}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      <DialogContent
        showCloseButton={false}
        className={cn(
          "glass-blur w-76 overflow-hidden rounded-3xl border-none bg-neutral-50/2 p-2",
          "max-w-none sm:max-w-none"
        )}
        aria-describedby={undefined}
      >
        <VisuallyHidden>
          <DialogTitle>Выбор даты</DialogTitle>
        </VisuallyHidden>
        {calendar}
      </DialogContent>
    </Dialog>
  );
}
